import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";

import { PageHero, Section } from "@/components/site/section";
import { NewsCard } from "@/components/site/cards";
import { newsByCategoryQuery } from "@/lib/site-queries";

export const Route = createFileRoute("/news")({
  head: () => ({
    meta: [
      { title: "Latest News | Argentinian.Soccer" },
      {
        name: "description",
        content:
          "Latest Argentina football news: Europe, Primera División, transfers, National Team, articles, interviews, video games and more.",
      },
      { property: "og:title", content: "Latest News | Argentinian.Soccer" },
      { property: "og:url", content: "/news" },
    ],
    links: [{ rel: "canonical", href: "/news" }],
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(newsByCategoryQuery(undefined, 48)),
  component: NewsPage,
});

const SECTIONS: { key: string; label: string; subs?: { key: string; label: string }[] }[] = [
  { key: "europe", label: "Europe" },
  { key: "primera", label: "Primera División" },
  { key: "rest_of_world", label: "Rest Of World" },
  { key: "transfers", label: "Transfers" },
  {
    key: "national_team",
    label: "National Team",
    subs: [
      { key: "world_cup", label: "World Cup" },
      { key: "u20", label: "U20" },
      { key: "u17", label: "U17" },
      { key: "u15", label: "U15" },
      { key: "women", label: "Women" },
    ],
  },
  { key: "articles", label: "Articles" },
  { key: "interviews", label: "Interviews" },
  { key: "video_games", label: "Video Games" },
  { key: "scandals", label: "Scandals" },
];

function NewsPage() {
  const { data: all } = useSuspenseQuery(newsByCategoryQuery(undefined, 48));

  return (
    <>
      <PageHero
        eyebrow="News"
        title="Latest News"
        description="Every story across European leagues, the Primera División, transfers, the National Team and more."
      />
      {SECTIONS.map((s) => {
        const items = all.filter((a) => a.category === s.key).slice(0, 6);
        return (
          <Section key={s.key} id={s.key} eyebrow={s.label} title={s.label}>
            {s.subs && (
              <div className="mb-5 flex flex-wrap gap-2">
                {s.subs.map((sub) => (
                  <span
                    key={sub.key}
                    className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground"
                  >
                    {sub.label}
                  </span>
                ))}
              </div>
            )}
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No stories yet in this section.</p>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {items.map((a) => (
                  <NewsCard key={a.id} article={a} />
                ))}
              </div>
            )}
          </Section>
        );
      })}
    </>
  );
}
